import React, { useState } from 'react';
import { Settings, ChevronDown, ChevronRight, Trash2, History } from 'lucide-react';
import { z } from 'zod';
import type { WorkflowFormState } from './WorkflowBuilderTypes.js';
import {
  type ChoiceOption,
  Form,
  FormFullWidth,
  FormSection,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
} from '../../components/form';
import { zWorkflowCreate } from '../../generated/api/zod.gen.js';
import { useBuilderForm } from './useBuilderForm.js';
import { Button } from '../../components/ui';
import { FieldChangeIndicator } from '../../components/ui/FieldChangeIndicator.js';
import { ScheduleField } from '../../components/ui/ScheduleField.js';
import type { FieldChange } from '../../hooks/useFieldChangeTracker.js';
import { useSnapshots, usePurgeSnapshots } from '../../hooks/useSnapshots.js';
import { WorkflowVersionHistory } from './WorkflowVersionHistory.js';
import { ContractHealthPanel } from '../../components/workflow/ContractHealthPanel.js';

export interface WorkflowSettingsFormProps {
  form: WorkflowFormState;
  updateField: <K extends keyof WorkflowFormState>(field: K, value: WorkflowFormState[K]) => void;
  changes?: Map<string, FieldChange>;
}

interface WorkflowSettingsPanelProps extends WorkflowSettingsFormProps {
  collapsed: boolean;
  onToggle: () => void;
  templateId?: number;
}

const CATEGORY_OPTIONS: ChoiceOption[] = [
  { value: 'equity-research', label: 'Equity research' },
  { value: 'macro-analysis', label: 'Macro analysis' },
  { value: 'crypto-research', label: 'Crypto research' },
  { value: 'risk-monitoring', label: 'Risk monitoring' },
  { value: 'portfolio-management', label: 'Portfolio management' },
  { value: 'code-generation', label: 'Code generation' },
  { value: 'custom', label: 'Custom' },
];

const STATUS_OPTIONS: ChoiceOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

/** The workflow's own fields (generated create schema); status and tags are held as the builder holds them. */
const settingsSchema = zWorkflowCreate
  .pick({ name: true, description: true, category: true })
  .extend({ status: z.string(), tags: z.string() });

type SettingsKey = 'name' | 'description' | 'category' | 'status' | 'tags';
const isSettingsKey = (name: string): name is SettingsKey =>
  name === 'name' || name === 'description' || name === 'category' || name === 'status' || name === 'tags';

export const WorkflowSettingsForm: React.FC<WorkflowSettingsFormProps> = ({ form, updateField, changes }) => {
  const settings = useBuilderForm(
    settingsSchema,
    {
      name: form.name,
      description: form.description,
      category: form.category,
      status: form.status,
      tags: form.tags,
    },
    (name, all) => {
      if (isSettingsKey(name)) updateField(name, all[name] ?? '');
    },
  );
  return (
    <Form form={settings} onSubmit={() => undefined} className="px-4 pb-4">
      <FormSection title="Workflow details">
        <div className="relative">
          <TextFormField<SettingsValues> name="name" label="Name" required />
          <FieldChangeIndicator fieldPath="name" changes={changes} />
        </div>
        <div className="relative">
          <SelectFormField<SettingsValues> name="category" label="Category" options={CATEGORY_OPTIONS} />
          <FieldChangeIndicator fieldPath="category" changes={changes} />
        </div>
        <div className="relative">
          <SelectFormField<SettingsValues> name="status" label="Status" options={STATUS_OPTIONS} />
          <FieldChangeIndicator fieldPath="status" changes={changes} />
        </div>
        <div className="relative">
          <TextFormField<SettingsValues> name="tags" label="Tags" placeholder="equity, ai-driven" />
          <FieldChangeIndicator fieldPath="tags" changes={changes} />
        </div>
        <FormFullWidth>
          <div className="relative">
            <ScheduleField
              label="Schedule"
              value={form.cronSchedule}
              onChange={(v) => updateField('cronSchedule', v)}
              enabled={form.scheduleEnabled}
              onEnabledChange={(v) => updateField('scheduleEnabled', v)}
            />
            <FieldChangeIndicator fieldPath="cronSchedule" changes={changes} />
          </div>
        </FormFullWidth>
        <FormFullWidth>
          <div className="relative">
            <TextAreaFormField<SettingsValues>
              name="description"
              label="Description"
              rows={3}
              placeholder="Describe what this workflow does..."
            />
            <FieldChangeIndicator fieldPath="description" changes={changes} />
          </div>
        </FormFullWidth>
      </FormSection>
    </Form>
  );
};

type SettingsValues = z.input<typeof settingsSchema>;

export const WorkflowSettingsPanel: React.FC<WorkflowSettingsPanelProps> = ({
  collapsed,
  onToggle,
  form,
  updateField,
  changes,
  templateId,
}) => (
  <div className="shrink-0 overflow-hidden rounded-xl border bg-surface">
    <Button
      variant="ghost"
      size="sm"
      onClick={onToggle}
      aria-expanded={!collapsed}
      className="h-auto w-full justify-between rounded-none px-4 py-2.5"
    >
      <h3 className="flex items-center gap-2 font-semibold text-heading text-sm">
        <Settings size={14} /> Settings
      </h3>
      {collapsed ? <ChevronRight size={14} className="text-faint" /> : <ChevronDown size={14} className="text-faint" />}
    </Button>
    {!collapsed && (
      <>
        <WorkflowSettingsForm form={form} updateField={updateField} changes={changes} />
        {templateId && <ContractHealthSection templateId={templateId} />}
        {templateId && <DebugDataSection templateId={templateId} />}
        {templateId && <VersionHistorySection templateId={templateId} />}
      </>
    )}
  </div>
);

const ContractHealthSection: React.FC<{ templateId: number }> = ({ templateId }) => (
  <div className="px-4 pb-4 pt-2 border-t">
    <h4 className="text-xs font-medium text-heading mb-2">Contract Health</h4>
    <ContractHealthPanel workflowId={templateId} />
  </div>
);

// ==========================================
// Debug Data Cleanup (workflow-level)
// ==========================================

const DebugDataSection: React.FC<{ templateId: number }> = ({ templateId }) => {
  const { data: snapshots } = useSnapshots(templateId);
  const purgeMutation = usePurgeSnapshots();
  const [showConfirm, setShowConfirm] = useState(false);

  const totalCount = snapshots?.data?.length ?? 0;

  return (
    <div className="px-4 pb-4 pt-2 border-t">
      <h4 className="text-xs font-medium text-heading mb-2">Debug Data</h4>
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted">
          {totalCount} snapshot{totalCount !== 1 ? 's' : ''} across all steps
        </span>
        {!showConfirm ? (
          <Button variant="ghost" size="sm" onClick={() => setShowConfirm(true)}>
            <Trash2 size={12} /> Purge old snapshots
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-warning">Delete all unpinned snapshots older than 30 days?</span>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                purgeMutation.mutate({ templateId: templateId, unpinnedOnly: true, olderThanDays: 30 });
                setShowConfirm(false);
              }}
            >
              Confirm
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
          </div>
        )}
      </div>
      {purgeMutation.isSuccess && purgeMutation.data && (
        <div className="text-xs text-success mt-1">
          Deleted {purgeMutation.data.deletedCount} snapshot{purgeMutation.data.deletedCount !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

// ==========================================
// Version History Section
// ==========================================

const VersionHistorySection: React.FC<{ templateId: number }> = ({ templateId }) => {
  const [showHistory, setShowHistory] = useState(false);

  return (
    <div className="px-4 pb-4 pt-2 border-t">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-medium text-heading">Version History</h4>
        <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}>
          <History size={12} />
          {showHistory ? 'Hide' : 'Show'} versions
        </Button>
      </div>
      {showHistory && (
        <WorkflowVersionHistory
          workflowId={templateId}
          onClose={() => setShowHistory(false)}
          onRestored={() => window.location.reload()}
        />
      )}
    </div>
  );
};
